import React from 'react';
import PropTypes from 'prop-types';
import { cloneObject } from '../../../common/utils';
import { Message } from '../../lists/SystemMessages';

import {
  allowedInputTypes,
  allowedDescriptionType,
  allowedTextAreaResizingOptions
} from './_allowedPropTypes';

import InputContainer from './_InputContainer';
import InputFieldDefault from './_InputFieldDefault';
import InputFieldDate from './_InputFieldDate';
import InputFieldAddress from './_InputFieldAddress';
import InputFieldSelect from './_InputFieldSelect';
import InputFieldSelectAsync from './_InputFieldSelectAsync';
import InputFieldCheckbox from './_InputFieldCheckbox';
import InputFieldPassword from './_InputFieldPassword';
import InputFieldTextArea from './_InputFieldTextArea';

function createMessage(errorMessage) {
  if (!errorMessage) {
    return null;
  }

  const message = typeof (errorMessage) === 'object'
    ? Object.values(errorMessage).join(' ')
    : errorMessage;

  return new Message('error', message);
}

function processInputProps(unprocessedProps) {
  const processedProps = cloneObject(unprocessedProps);

  const {
    inputAttributes: {
      placeholder,
      type,
      inputRef
    },
    description,
    label
  } = processedProps;

  if (placeholder === true || !(placeholder || label)) {
    processedProps.inputAttributes.placeholder = description;
  }

  if (inputRef) {
    processedProps.inputAttributes.ref = inputRef;
    delete processedProps.inputAttributes.inputRef;
  }

  if (label === true || type === 'checkbox') {
    processedProps.label = description;
  }

  return processedProps;
}

const CustomInputController = props => {
  const {
    errorMessage,
    inputAttributes: {
      type: inputType
    }
  } = props;

  const processedProps = processInputProps(props);

  processedProps.message = createMessage(errorMessage);

  let InputFieldComponent = null;

  switch (inputType) {
    case 'checkbox':
    case 'radio':
      InputFieldComponent = InputFieldCheckbox;
      break;

    case 'select':
      InputFieldComponent = InputFieldSelect;
      break;

    case 'select-async':
      InputFieldComponent = InputFieldSelectAsync;
      break;

    case 'date':
    case 'date-picker':
      InputFieldComponent = InputFieldDate;
      break;

    case 'address':
      InputFieldComponent = InputFieldAddress;
      break;

    case 'password':
      InputFieldComponent = InputFieldPassword;
      break;

    case 'textarea':
      InputFieldComponent = InputFieldTextArea;
      break;

    default:
      InputFieldComponent = InputFieldDefault;
  }

  return (
    <InputContainer {...processedProps}>
      <InputFieldComponent {...processedProps} />
    </InputContainer>
  );
};

CustomInputController.propTypes = {
  inputAttributes: PropTypes.shape({
    type: PropTypes.oneOf(allowedInputTypes),
    placeholder: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.bool
    ]),
    name: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    onBlur: PropTypes.func,
    value: PropTypes.oneOfType([ // generated by Formik (except if type is 'radio')
      PropTypes.arrayOf(PropTypes.string), // for select with isMulti === true
      PropTypes.string,
      PropTypes.number,
      PropTypes.bool,
      PropTypes.shape({
        address: PropTypes.string.isRequired,
        location: PropTypes.shape({
          latitude: PropTypes.number.isRequired,
          longitude: PropTypes.number.isRequired
        }).isRequired
      })
    ]).isRequired
  }),
  isEnhanced: PropTypes.bool,
  errorMessage: PropTypes.string, // generated by Formik controller
  description: allowedDescriptionType,
  isSwappedAlign: PropTypes.bool,
  dateDefault: PropTypes.object,
  dateMin: PropTypes.object,
  dateMax: PropTypes.object,
  isDatePickerOpen: PropTypes.bool,
  inputRef: PropTypes.object,
  checkPasswordStrength: PropTypes.bool,
  options: PropTypes.array, // for select-type
  noOptionsMessage: PropTypes.func, // for select-type
  onInputChange: PropTypes.func, // for select-type
  createOptionMessage: PropTypes.func, // for select-type when isCreatable is set true
  isMulti: PropTypes.bool, // for select-type
  isCreatable: PropTypes.bool, // for select-type
  resizability: PropTypes.oneOf(allowedTextAreaResizingOptions), // for textarea-type
  placeSuggestionMeterRadius: PropTypes.number, // for address-type
  placeSuggestionTypes: PropTypes.array, // for address-type
  placeSearchLocation: PropTypes.object, // for address-type
  onAddressChange: PropTypes.func, // for address-type
  onAddressSelect: PropTypes.func, // for address-type
  onGeocodeProcess: PropTypes.func, // for address-type
  suggestionListTitle: PropTypes.string, // for address-type
  onDayClick: PropTypes.func, // for date-type
  wrapperClasses: PropTypes.arrayOf(PropTypes.string),
  label: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.string,
    PropTypes.bool
  ])
};

export default CustomInputController;